// src/pages/public/publicPageHelpers.ts
import { citySlug } from '../../seo/core/slug';
import type { LocationInventoryRow, SeoInventory } from '../../seo/types';

/**
 * Recover a city's display spelling.
 *
 * Preference order matters: the real spelling from marketplace inventory always
 * wins. Title-casing the URL slug is only a fallback for a city with no live
 * inventory — those pages are noindex anyway, and a location name derived from
 * the URL is not a fabricated marketplace claim (unlike a count or a price,
 * which are never synthesised).
 */
export function resolveCityDisplayName(
  inventory: SeoInventory | null,
  stateCode: string,
  slug: string
): string {
  const rows = inventory?.locations || [];
  const match = rows.find(
    (row) =>
      citySlug(row.city) === slug &&
      String(row.state || '').toUpperCase() === stateCode.toUpperCase()
  );
  if (match) return match.city;
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/** All inventory rows for one state. */
export function citiesInState(
  inventory: SeoInventory | null,
  stateCode: string
): LocationInventoryRow[] {
  return (inventory?.locations || []).filter(
    (row) => String(row.state || '').toUpperCase() === stateCode.toUpperCase()
  );
}

/** One city's inventory row, or undefined when the city has no live supply. */
export function cityRow(
  inventory: SeoInventory | null,
  stateCode: string,
  slug: string
): LocationInventoryRow | undefined {
  return citiesInState(inventory, stateCode).find((row) => citySlug(row.city) === slug);
}

/** Distinct states present in inventory, with their stylist counts. */
export function statesWithInventory(
  inventory: SeoInventory | null
): Array<{ state: string; stylistCount: number; cities: LocationInventoryRow[] }> {
  const byState = new Map<string, { state: string; stylistCount: number; cities: LocationInventoryRow[] }>();
  (inventory?.locations || []).forEach((row) => {
    const code = String(row.state || '').toUpperCase();
    if (!code) return;
    if (!byState.has(code)) byState.set(code, { state: code, stylistCount: 0, cities: [] });
    const bucket = byState.get(code)!;
    bucket.cities.push(row);
    bucket.stylistCount += Number(row.stylistCount) || 0;
  });
  return Array.from(byState.values()).sort((a, b) => b.stylistCount - a.stylistCount);
}
